import React, { Component } from 'react';
import Link from 'common/components/LocalizedLink/LocalizedLink';
import { injectIntl } from 'react-intl';
import { observable, computed, when } from 'mobx';
import { observer } from 'mobx-react';
import OptimizeImagesService from 'services/OptimizeImages.service';
import LanguageService from 'services/Language.service';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import { LOAD_STATES } from 'common/consts/common';
import {
  CONTACT,
  COMPANIES_COLORS,
  COMPANIES_WITH_PROJECTS
} from 'common/consts/common';

import { ANCHOR } from 'common/consts/common';
import styles from './ChoosenCompanyView.module.scss';

import messages from './ChoosenCompanyView.intl';

import { Container, Title } from 'common/components/Layout/Layout';
import OurProducts from 'common/components/Products/OurProducts';
import CompanyInfoBox from './components/CompanyInfoBox/CompanyInfoBox';
import SmallInfoBox from './components/SmallInfoBox/SmallInfoBox';
import PartnerBox from './components/PartnerBox/PartnerBox';

import CompaniesStore from 'stores/Companies';
import Placeholder from 'common/components/Placeholder/Placeholder';
import ContentBar from 'common/components/Placeholder/components/ContentBar/ContentBar';
import ChoosenCompanyPlaceholder from './placeholder/ChoosenCompanyPlaceholder';

@injectIntl
@observer
export default class ChoosenCompanyView extends Component {
  choosenCompany = {};
  loadState = observable.box('');
  currentLanguage = LanguageService.getLang();

  async componentDidMount() {
    this.loadState.set(LOAD_STATES.LOADING);

    const companyName = this.props.match.params.slug;

    when(
      () => CompaniesStore.initialFetchDone.get(),
      async () => {
        this.choosenCompany = await CompaniesStore.getCompanyBySlug(
          companyName
        );
        this.loadState.set(LOAD_STATES.LOADED);

        if (!this.choosenCompany) {
          const langPrefix = LanguageService.getLangPrefix();
          this.props.history.push(
            `${langPrefix}${ANCHOR.COMPANIES[this.currentLanguage]}`
          );
        }
      }
    );
  }

  @computed
  get isLoaded() {
    return this.loadState.get() === LOAD_STATES.LOADED;
  }

  hasCompanyProjects(company) {
    return COMPANIES_WITH_PROJECTS.some(
      companyWithProjects => companyWithProjects === company.codename
    );
  }

  appendClassForDots(color) {
    switch (color) {
      case COMPANIES_COLORS.TENDERHUT:
        return styles.Tenderhut;
      case COMPANIES_COLORS.SOFTWAREHUT:
        return styles.Softwarehut;
      case COMPANIES_COLORS.EXTRAHUT:
        return styles.Extrahut;
      case COMPANIES_COLORS.LEGALHUT:
        return styles.Legalhut;
      case COMPANIES_COLORS.PROTECTHUT:
        return styles.Protecthut;
      case COMPANIES_COLORS.SOLUTION4LABS:
        return styles.Solution4Labs;
      default:
        return '';
    }
  }

  render() {
    const company = this.choosenCompany;

    if (!company) {
      return null;
    }

    const isAchievmentsDescriptionEmpty =
      company.achievmentsDescription === '<p><br></p>';

    return (
      <div>
        <Container>
          <Placeholder
            component={<ContentBar className={styles.ContentBarPlaceholder} />}
            ready={this.isLoaded}
          >
            <div className={styles.Header}>
              {this.isLoaded && (
                <Title title={company.name} className={styles.HeaderTitle} />
              )}
              <Link
                to={ANCHOR.COMPANIES[this.currentLanguage]}
                className={styles.HeaderLink}
              >
                <FontAwesomeIcon
                  className={styles.HeaderArrow}
                  icon={['fas', 'angle-up']}
                />
                <span className={styles.HeaderLinkText}>
                  {this.props.intl.formatMessage(messages.showAllCompanies)}
                </span>
              </Link>
            </div>
          </Placeholder>
        </Container>
        <Placeholder
          component={<ChoosenCompanyPlaceholder />}
          ready={this.isLoaded}
        >
          <div className={styles.CompanyCardContainer}>
            <div className={styles.CompanyCard}>
              <div className={styles.CompanyCardContent}>
                <img
                  className={styles.CompanyLogo}
                  src={OptimizeImagesService.adjustImageSize(company.logo)}
                  alt="Company-logo"
                />
                <div className={styles.CompanyCardDescription}>
                  {company.descriptionShort}
                </div>
              </div>
            </div>
            <div
              className={styles.CompanyBackgroundImage}
              style={{
                backgroundImage: `url(${OptimizeImagesService.adjustImageSize(
                  company.photo
                )})`
              }}
            />
            {this.isLoaded && (
              <div
                className={styles.CompanyBackgroundInfo}
                style={{ backgroundColor: company.color }}
              >
                <Container className={styles.CompanyInfoBoxContainer}>
                  {company.infoBoxesData.map(data => (
                    <CompanyInfoBox key={data.id} data={data} />
                  ))}
                </Container>
              </div>
            )}
          </div>
          <Container>
            <div className={styles.CompanyInfo}>
              {!isAchievmentsDescriptionEmpty && (
                <div className={styles.CompanyInfoDescription}>
                  <div
                    className={`${styles.ContentBox} ${this.appendClassForDots(
                      company.color
                    )}`}
                    dangerouslySetInnerHTML={{
                      __html: company.achievmentsDescription
                    }}
                  />
                </div>
              )}
              {company.achievmentsImage && (
                <div className={styles.CompanyInfoImageBox}>
                  <img
                    src={OptimizeImagesService.adjustImageSize(
                      company.achievmentsImage
                    )}
                    className={styles.CompanyInfoImage}
                    alt="logo"
                  />
                </div>
              )}
            </div>
          </Container>
        </Placeholder>
        <div className={styles.SpecializationBackgroundContainer}>
          <Container>
            <h3 className={styles.SpecializationHeading}>
              {this.props.intl.formatMessage(messages.ourSpecialities)}
            </h3>
            {this.isLoaded && (
              <div className={styles.SpecializationContent}>
                {company.specializationData.map(data => (
                  <div
                    key={data.id}
                    className={styles.SpecializationContentBoxColumn}
                  >
                    <SmallInfoBox data={data} />
                  </div>
                ))}
              </div>
            )}
          </Container>
        </div>
        {this.isLoaded && (
          <OurProducts
            changeHeading={this.hasCompanyProjects(company)}
            products={company.products}
          />
        )}
        <div className={styles.OurPartnersBackgroundContainer}>
          <Container className={styles.PartnersContainer}>
            <h2 className={styles.PartnersHeaderTitle}>
              {this.props.intl.formatMessage(messages.ourPartners)}
            </h2>
            <div className={styles.PartnersContentBox}>
              {this.isLoaded && (
                <div className={styles.PartnersList}>
                  {company.partners.map(partner => (
                    <PartnerBox key={partner.id} partner={partner} />
                  ))}
                </div>
              )}
              <div className={styles.PartnersJoinUsWrapper}>
                <div className={styles.PartnersJoinUsBox}>
                  <p className={styles.PartnersJoinUsText}>
                    {this.props.intl.formatMessage(messages.joinUsQuestion)}
                  </p>
                  <a
                    href={`mailto:${CONTACT.JOIN_US}`}
                    className={styles.PartnersJoinUsLink}
                  >
                    {this.props.intl.formatMessage(messages.joinUsLink)}
                  </a>
                </div>
              </div>
            </div>
          </Container>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/views/ChoosenCompany/ChoosenCompanyView.js